import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ConfigProvider } from "antd";
import { LANGUAGES, DEFAULT_LANGUAGE_CODE } from "./languages";

export const STORAGE_KEY = "appLanguage";

const findLanguage = (code) =>
   LANGUAGES.find((language) => language.code === code) ||
   LANGUAGES.find((language) => language.code === DEFAULT_LANGUAGE_CODE);

/* Default value keeps any consumer safe to render even outside a provider
   (e.g. tests) — t() just falls back to returning the raw key. */
const LanguageContext = createContext({
   language: findLanguage(DEFAULT_LANGUAGE_CODE),
   languages: LANGUAGES,
   setLanguageCode: () => {},
   t: (key) => key
});

export const useTranslation = () => useContext(LanguageContext);

const readInitialLanguageCode = () => {
   try {
      return localStorage.getItem(STORAGE_KEY) || DEFAULT_LANGUAGE_CODE;
   } catch (e) {
      return DEFAULT_LANGUAGE_CODE;
   }
};

/* Same lookup as t() above, for the few call sites (Redux thunks, etc.) that
   fire user-facing messages outside the React tree and can't use hooks. */
export const translate = (key) => findLanguage(readInitialLanguageCode()).dictionary[key] || key;

export const LanguageProvider = ({ children }) => {
   const [languageCode, setLanguageCodeState] = useState(readInitialLanguageCode);
   const language = useMemo(() => findLanguage(languageCode), [languageCode]);

   const setLanguageCode = useCallback((code) => {
      const next = findLanguage(code).code;
      setLanguageCodeState(next);
      try {
         localStorage.setItem(STORAGE_KEY, next);
      } catch (e) {
         /* localStorage unavailable — keep in-memory state only */
      }
   }, []);

   useEffect(() => {
      document.documentElement.lang = language.code;
      document.documentElement.dir = language.dir;
   }, [language]);

   const t = useCallback((key) => language.dictionary[key] || key, [language]);

   const value = useMemo(() => ({ language, languages: LANGUAGES, setLanguageCode, t }), [
      language,
      setLanguageCode,
      t
   ]);

   return (
      <LanguageContext.Provider value={value}>
         {/* Translates antd's OWN built-in text (Table pagination, DatePicker,
             Popconfirm OK/Cancel, Empty "No data", etc.) — separate from our
             own `t()` dictionary above, which only covers text we wrote. */}
         <ConfigProvider locale={language.antdLocale} direction={language.dir}>
            {children}
         </ConfigProvider>
      </LanguageContext.Provider>
   );
};

export default LanguageContext;
